import React, { useEffect, useState } from "react";
import useSWR from "swr";
import Overview, { fetcher } from "../../components/Overview/Overview";
import { getCompetenceProfiles } from "../../queries/competence_profiles";
import moment from "moment";
import Dialog from "rc-dialog";
import Quickview from "../../components/Quickview";
import { CompetenceProfileOverviewDefinitionBase } from "./CompetenceProfileOverview";

export const CompetenceProfileSelector = React.memo((props) => {
    // Top level vars (Course items)
    const [competenceProfileUUID, setCompetenceProfileUUID] = useState(props?.competenceProfileDetails?.uuid);

    const [confirmSwitch, setConfirmSwitch] = useState(false);

    // Filtering related
    const [typeFilter, setTypeFilter] = useState("");
    const [kindFilter, setKindFilter] = useState("");
    const [languageFilter, setLanguageFilter] = useState("");
    const [eLearningFilter, setELearningFilter] = useState(null);
    const [employerSpecificFilter, setEmployerSpecificFilter] = useState(null);
    const [trainerSpecificFilter, setTrainerSpecificFilter] = useState(null);
    const [cpNameFilter, setCpNameFilter] = useState("");

    const [disableTrainerSpecific, setDisableTrainerSpecific] = useState(false);

    const [stats, setStats] = useState({
        type: [],
        repetition: [],
        language: [],
        contains_elearning: [],
        allow_employer_specific: [],
        trainers: []
    });

    const emptySelection = [
        typeFilter === "",
        kindFilter === "",
        languageFilter === "",
        eLearningFilter === null,
        employerSpecificFilter === null,
        trainerSpecificFilter === null,
        cpNameFilter === ""
    ].every((o) => o === true);

    const [courseEvents, setCourseEvents] = useState(false);
    const [scheduledEvents, setScheduledEvents] = useState(false);

    // filter object for SWR
    const [filter, setFilter] = useState({});

    // Options used in the competence profile filtering
    const languageOptions = [
        {
            value: "nl",
            display_name: "Nederlands"
        },
        {
            value: "en",
            display_name: "Engels"
        }
    ];

    // Options used in the competence profile filtering
    const elearningOptions = [
        {
            value: true,
            display_name: "E-learning"
        },
        {
            value: false,
            display_name: "Geen E-learning"
        }
    ];

    const employerSpecificOptions = [
        {
            value: true,
            display_name: "Bedrijfsspecifiek"
        },
        {
            value: false,
            display_name: "Niet Bedrijfsspecifiek"
        }
    ];

    const trainerSpecificOptions = [
        {
            value: true,
            display_name: "Opleiderspecifiek"
        }
    ];

    const { data: typeOptions } = useSWR(() => "/v1/competenceprofiletypes/", fetcher);
    const kind_options = [
        {
            value: "base",
            bool: false,
            display_name: "Basis"
        },
        {
            value: "repetition",
            bool: true,
            display_name: "Herhaling"
        }
    ];

    const now = moment();

    const minMaxDateText = (minDate, maxDate) => {
        const min = minDate ? moment(minDate, "YYYY-MM-DD") : false;
        const max = maxDate ? moment(maxDate, "YYYY-MM-DD") : false;

        if (min && max) {
            return `Beschikbaar tussen ${min.format("D MMM", ["nl"])} en ${max.format("D MMM", ["nl"])}`;
        }
        if (min && !max && min > moment()) {
            return `Beschikbaar vanaf ${min.format("D MMM", ["nl"])}`;
        }
        if (!min && max) {
            return `Beschikbaar tot ${max.format("D MMM", ["nl"])}`;
        }
    };

    const competenceProfilesSWR = getCompetenceProfiles(filter, false);
    const competenceProfiles = competenceProfilesSWR.data; // Filtered competence profiles

    useEffect(() => {
        if (props?.competenceProfileDetails?.uuid && props.competenceProfileDetails.uuid !== competenceProfileUUID) {
            setCompetenceProfileUUID(props.competenceProfileDetails.uuid);
        }
    }, [props?.competenceProfileDetails?.uuid]);

    useEffect(() => {
        if (competenceProfileUUID && competenceProfileUUID !== props?.competenceProfileDetails?.uuid) {
            if (props?.setReturnValue && typeof props?.setReturnValue === "function") {
                const cp = competenceProfiles?.find((o) => o.uuid === competenceProfileUUID);
                if (cp) {
                    props.setReturnValue(cp);
                }
            }
        }
    }, [competenceProfileUUID, competenceProfiles, props?.competenceProfileDetails?.uuid]);

    useEffect(() => {
        setFilter({
            filter: {
                is_active: 1,
                type: typeFilter ? typeFilter.slug : "",
                repetition: kindFilter?.value === "repetition" ? true : kindFilter?.value === "base" ? false : "",
                language: languageFilter && languageOptions?.length > 0 && languageOptions.find((o) => o.value === languageFilter.value) ? languageFilter.value : "",
                search: cpNameFilter,
                contains_elearning: eLearningFilter ? eLearningFilter.value : null,
                allow_employer_specific: employerSpecificFilter ? employerSpecificFilter.value : null,
                trainers: trainerSpecificFilter ? props.trainer.uuid : ""
            }
        });
    }, [eLearningFilter, employerSpecificFilter, typeFilter, kindFilter, languageFilter, trainerSpecificFilter, cpNameFilter, props.trainer]);

    useEffect(() => {
        // If uuid not in current selection of CP's, we should unselect the selected UUID
    }, [competenceProfileUUID, competenceProfiles]);

    useEffect(() => {
        if (props?.courseEvents) {
            setCourseEvents(props.courseEvents);
        }
        if (props?.scheduledEvents) {
            setScheduledEvents(props.scheduledEvents);
        }
    }, [props?.courseEvents, props?.scheduledEvents]);

    useEffect(() => {
        if (!(!competenceProfilesSWR.error && !competenceProfilesSWR?.data)) {
            const stats = {
                type: [],
                repetition: [],
                language: [],
                contains_elearning: [],
                allow_employer_specific: [],
                trainers: []
            };

            // determine filters to potentially disable`
            competenceProfiles?.forEach((competenceProfile) => {
                Object.keys(stats).forEach((key) => {
                    const val = competenceProfile[key];
                    if (val !== undefined && val !== null && !Array.isArray(val)) {
                        if (!stats[key].includes(competenceProfile[key])) {
                            stats[key].push(competenceProfile[key]);
                        }
                    }
                    if (Array.isArray(val)) {
                        if (val?.length === 0) {
                            stats[key] = [...stats[key], ""];
                        }
                        stats[key] = [...stats[key], ...val];
                    }
                });
            });
            stats.trainers = [...new Set(stats.trainers)];
            setStats(stats);
        }
    }, [competenceProfiles, competenceProfilesSWR]);

    useEffect(() => {
        if (disableTrainerSpecific === false && emptySelection && stats.trainers.length > 0 && stats.trainers.filter((o) => o !== "") < 1) {
            // there appear to be no trainer options for an empty filtering selection so we can disable "trainer specific"
            setDisableTrainerSpecific(true);
        }
    }, [disableTrainerSpecific, emptySelection, stats]);

    return (
        <>
            {confirmSwitch && (
                <Dialog
                    title={"Competentieprofiel wijzigen"}
                    onClose={() => setConfirmSwitch(false)}
                    visible={true}
                    animation="fade"
                    footer={[
                        <button
                            type="button"
                            className="btn btn-light"
                            key="no"
                            onClick={() => {
                                setConfirmSwitch(false);
                            }}
                        >
                            Nee, competentieprofiel niet wijzigen
                        </button>,
                        <button
                            type="button"
                            className="btn btn-primary"
                            key="yes"
                            onClick={() => {
                                setConfirmSwitch(false);
                                setCompetenceProfileUUID(confirmSwitch);
                            }}
                        >
                            Ja, wijzig het competentieprofiel
                        </button>
                    ]}
                >
                    <p className={"paragraph"}>
                        Door het wijzigen van het competentieprofiel zullen alle reeds ingevulde gegevens worden verwijderd.
                        <br />
                        <br />
                        Weet je zeker dat je het competentieprofiel wilt wijzigen?
                    </p>
                </Dialog>
            )}
            <h2 className="form-section__title">Selecteer het type cursus</h2>

            <div className="row">
                <div className="col-12">
                    <Quickview initialIndex={0}>
                        <Overview
                            classNamePrefix={"competence-selector"}
                            depricateOldStyling={true}
                            quickview={{
                                display: false,
                                identifier: "competence-profile-all",
                                name: "Alles"
                            }}
                            identifier={"competence-profile-all"}
                            definition={{
                                ...CompetenceProfileOverviewDefinitionBase,
                                columns: CompetenceProfileOverviewDefinitionBase.columns.filter((o) => o.columnIdentifier !== "trainers" || props?.multipleTrainers)
                            }}
                            contextData={{
                                typeOptions: typeOptions?.results || [],
                                trainer: props?.trainer,
                                competenceProfileUUID,
                                selectRow: (props) => {
                                    if (competenceProfileUUID !== props.uuid && (scheduledEvents || courseEvents)) {
                                        setConfirmSwitch(props.uuid);
                                    } else {
                                        setCompetenceProfileUUID(props.uuid);
                                    }
                                }
                            }}
                        />
                    </Quickview>
                </div>
            </div>
        </>
    );
});
